import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Key,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Copy,
  Eye,
  EyeOff,
  Clock,
  Activity,
  BarChart3,
  Shield,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  Zap,
  Globe,
  Lock,
} from 'lucide-react';
import { notify } from '@core/notifications/notify';
import ConfirmationModal from '@/components/ConfirmationModal';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

const AVAILABLE_SCOPES = ['read', 'write', 'admin', 'compliance', 'frameworks', 'users', 'reports'];
const EXPIRY_OPTIONS = [
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
  { label: '1 year', value: 365 },
  { label: 'Never', value: 0 },
];

interface ApiKey {
  id: number;
  key_name: string;
  key_prefix: string;
  scopes: string[];
  rate_limit_per_minute: number;
  is_active: boolean;
  last_used_at: string | null;
  last_used_ip: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

interface UsageLog {
  id: number;
  endpoint: string;
  method: string;
  status_code: number;
  ip_address: string;
  user_agent: string;
  response_time_ms: number;
  created_at: string;
}

interface KeyStats {
  total: number;
  active: number;
  revoked: number;
  totalCalls30d: number;
}

const ApiKeysManagement: React.FC = () => {
  const { user } = useAuth();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<KeyStats>({ total: 0, active: 0, revoked: 0, totalCalls30d: 0 });

  // Modals
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [usageOpen, setUsageOpen] = useState(false);
  const [createdKeyOpen, setCreatedKeyOpen] = useState(false);

  // Selected key
  const [selectedKey, setSelectedKey] = useState<ApiKey | null>(null);
  const [usageLogs, setUsageLogs] = useState<UsageLog[]>([]);
  const [usageLoading, setUsageLoading] = useState(false);

  // Create form
  const [createForm, setCreateForm] = useState({
    key_name: '',
    scopes: ['read'] as string[],
    rate_limit_per_minute: 60,
    expires_in_days: 90,
  });

  // Edit form
  const [editForm, setEditForm] = useState({
    key_name: '',
    scopes: [] as string[],
    rate_limit_per_minute: 60,
    is_active: true,
  });

  // Created key display
  const [createdFullKey, setCreatedFullKey] = useState('');
  const [keyCopied, setKeyCopied] = useState(false);

  // Saving
  const [saving, setSaving] = useState(false);

  // Search & filter
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'revoked' | 'expired'>('all');

  // Usage pagination
  const [usagePage, setUsagePage] = useState(1);
  const usagePerPage = 10;

  useEffect(() => {
    loadKeys();
  }, []);

  const loadKeys = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/settings/api-keys', { headers: authHeaders() });
      if (!res.ok) {
        setKeys([]);
        setStats({ total: 0, active: 0, revoked: 0, totalCalls30d: 0 });
        return;
      }
      const data = await res.json();
      const list: ApiKey[] = Array.isArray(data) ? data : data?.keys || [];
      setKeys(list);
      const now = new Date();
      const active = list.filter(k => k.is_active && !k.revoked_at && (!k.expires_at || new Date(k.expires_at) > now)).length;
      const revoked = list.filter(k => k.revoked_at).length;
      setStats({ total: list.length, active, revoked, totalCalls30d: data?.totalCalls30d || 0 });
    } catch {
      setKeys([]);
      setStats({ total: 0, active: 0, revoked: 0, totalCalls30d: 0 });
    } finally {
      setLoading(false);
    }
  };

  const createKey = async () => {
    setSaving(true);
    try {
      const body: any = {
        key_name: createForm.key_name,
        scopes: createForm.scopes,
        rate_limit_per_minute: createForm.rate_limit_per_minute,
      };
      if (createForm.expires_in_days > 0) {
        const d = new Date();
        d.setDate(d.getDate() + createForm.expires_in_days);
        body.expires_at = d.toISOString();
      }
      const res = await fetch('/api/settings/api-keys', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        setCreatedFullKey(data.key || data.api_key || data.full_key || '');
        setKeyCopied(false);
        setCreateOpen(false);
        setCreatedKeyOpen(true);
        setCreateForm({ key_name: '', scopes: ['read'], rate_limit_per_minute: 60, expires_in_days: 90 });
        loadKeys();
        notify.success('API key created successfully');
      } else {
        const err = await res.json().catch(() => ({}));
        notify.error(err.message || 'Failed to create API key');
      }
    } catch {
      notify.error('Failed to create API key');
    } finally {
      setSaving(false);
    }
  };

  const updateKey = async () => {
    if (!selectedKey) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/settings/api-keys/${selectedKey.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(editForm),
      });
      if (res.ok) {
        notify.success('API key updated');
        setEditOpen(false);
        loadKeys();
      } else {
        const err = await res.json().catch(() => ({}));
        notify.error(err.message || 'Failed to update API key');
      }
    } catch {
      notify.error('Failed to update API key');
    } finally {
      setSaving(false);
    }
  };

  const revokeKey = async () => {
    if (!selectedKey) return;
    try {
      const res = await fetch(`/api/settings/api-keys/${selectedKey.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.ok) {
        notify.success('API key revoked');
        setRevokeOpen(false);
        loadKeys();
      } else {
        notify.error('Failed to revoke API key');
      }
    } catch {
      notify.error('Failed to revoke API key');
    }
  };

  const loadUsage = async (key: ApiKey) => {
    setSelectedKey(key);
    setUsageOpen(true);
    setUsageLoading(true);
    setUsagePage(1);
    try {
      const res = await fetch(`/api/settings/api-keys/${key.id}/usage`, { headers: authHeaders() });
      if (!res.ok) {
        setUsageLogs([]);
        return;
      }
      const data = await res.json();
      setUsageLogs(Array.isArray(data) ? data : data?.logs || []);
    } catch {
      setUsageLogs([]);
    } finally {
      setUsageLoading(false);
    }
  };

  const openEdit = (key: ApiKey) => {
    setSelectedKey(key);
    setEditForm({
      key_name: key.key_name,
      scopes: [...(key.scopes || [])],
      rate_limit_per_minute: key.rate_limit_per_minute,
      is_active: key.is_active,
    });
    setEditOpen(true);
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(createdFullKey);
      setKeyCopied(true);
      notify.success('Key copied to clipboard');
    } catch {
      notify.error('Failed to copy');
    }
  };

  const toggleScope = (scope: string, form: string[], setter: (s: string[]) => void) => {
    setter(form.includes(scope) ? form.filter(s => s !== scope) : [...form, scope]);
  };

  const getKeyStatus = (key: ApiKey): { label: string; color: string } => {
    if (key.revoked_at) return { label: 'Revoked', color: 'bg-red-100 text-red-700' };
    if (key.expires_at && new Date(key.expires_at) < new Date()) return { label: 'Expired', color: 'bg-amber-100 text-amber-700' };
    if (key.is_active) return { label: 'Active', color: 'bg-green-100 text-green-700' };
    return { label: 'Inactive', color: 'bg-slate-100 text-slate-600' };
  };

  const getMethodColor = (method: string) => {
    const colors: Record<string, string> = { GET: 'bg-green-100 text-green-700', POST: 'bg-blue-100 text-blue-700', PUT: 'bg-amber-100 text-amber-700', DELETE: 'bg-red-100 text-red-700', PATCH: 'bg-purple-100 text-purple-700' };
    return colors[method] || 'bg-slate-100 text-slate-600';
  };

  const getStatusColor = (code: number) => {
    if (code < 300) return 'text-green-600';
    if (code < 400) return 'text-amber-600';
    return 'text-red-600';
  };

  // Filtered keys
  const filteredKeys = keys.filter(k => {
    const matchesSearch = !searchTerm || k.key_name.toLowerCase().includes(searchTerm.toLowerCase()) || k.key_prefix.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;
    if (filterStatus === 'all') return true;
    const status = getKeyStatus(k).label.toLowerCase();
    return status === filterStatus;
  });

  const paginatedUsage = usageLogs.slice((usagePage - 1) * usagePerPage, usagePage * usagePerPage);
  const totalUsagePages = Math.ceil(usageLogs.length / usagePerPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Key className="text-blue-600" size={28} />
            API Keys Management
          </h1>
          <p className="text-slate-500 mt-1">Create and manage API keys for programmatic access</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={loadKeys} className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors">
            <RefreshCw size={16} />
            Refresh
          </button>
          <button onClick={() => { setCreateForm({ key_name: '', scopes: ['read'], rate_limit_per_minute: 60, expires_in_days: 90 }); setCreateOpen(true); }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors">
            <Plus size={16} />
            Create Key
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total Keys</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{stats.total}</p>
            </div>
            <div className="p-3 rounded-xl bg-blue-100"><Key className="text-blue-600" size={24} /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Active Keys</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{stats.active}</p>
            </div>
            <div className="p-3 rounded-xl bg-green-100"><CheckCircle className="text-green-600" size={24} /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Revoked Keys</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{stats.revoked}</p>
            </div>
            <div className="p-3 rounded-xl bg-red-100"><XCircle className="text-red-600" size={24} /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">API Calls (30d)</p>
              <p className="text-2xl font-bold text-indigo-600 mt-1">{stats.totalCalls30d.toLocaleString()}</p>
            </div>
            <div className="p-3 rounded-xl bg-indigo-100"><Activity className="text-indigo-600" size={24} /></div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search by name or prefix..." className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value as any)} className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm">
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="revoked">Revoked</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      {/* Keys Table */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Key Prefix</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Scopes</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Rate Limit</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Last Used</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Created</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredKeys.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-slate-400">
                    <Key size={40} className="mx-auto mb-2 opacity-50" />
                    <p>{keys.length === 0 ? 'No API keys created yet' : 'No keys match your filters'}</p>
                  </td>
                </tr>
              ) : filteredKeys.map(key => {
                const status = getKeyStatus(key);
                return (
                  <tr key={key.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900">{key.key_name}</td>
                    <td className="px-4 py-3">
                      <code className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs font-mono">{key.key_prefix}...</code>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(key.scopes || []).slice(0, 3).map(s => (
                          <span key={s} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">{s}</span>
                        ))}
                        {(key.scopes || []).length > 3 && (
                          <span className="px-1.5 py-0.5 bg-slate-100 text-slate-500 text-xs rounded">+{key.scopes.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{key.rate_limit_per_minute}/min</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}>{status.label}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{key.last_used_at ? new Date(key.last_used_at).toLocaleString() : 'Never'}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{new Date(key.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => loadUsage(key)} title="View Usage" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-blue-600 transition-colors">
                          <BarChart3 size={16} />
                        </button>
                        {!key.revoked_at && (
                          <>
                            <button onClick={() => openEdit(key)} title="Edit" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-amber-600 transition-colors">
                              <Edit size={16} />
                            </button>
                            <button onClick={() => { setSelectedKey(key); setRevokeOpen(true); }} title="Revoke" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-red-600 transition-colors">
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Key Modal */}
      {createOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-[9999]">
          <div className="relative min-h-full flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg ml-64">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-xl"><Plus className="text-white" size={22} /></div>
                    <div>
                      <h3 className="text-lg font-bold text-white">Create API Key</h3>
                      <p className="text-white/70 text-sm">Generate a new API key for programmatic access</p>
                    </div>
                  </div>
                  <button onClick={() => setCreateOpen(false)} className="p-2 bg-white/20 hover:bg-white/30 rounded-xl"><X className="text-white" size={20} /></button>
                </div>
              </div>
              <div className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Key Name *</label>
                  <input type="text" value={createForm.key_name} onChange={e => setCreateForm({ ...createForm, key_name: e.target.value })} placeholder="e.g. Production API, CI/CD Pipeline" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Scopes *</label>
                  <div className="grid grid-cols-2 gap-2">
                    {AVAILABLE_SCOPES.map(scope => (
                      <label key={scope} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${createForm.scopes.includes(scope) ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                        <input type="checkbox" checked={createForm.scopes.includes(scope)} onChange={() => toggleScope(scope, createForm.scopes, s => setCreateForm({ ...createForm, scopes: s }))} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                        <span className="text-sm capitalize">{scope}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Rate Limit (per minute)</label>
                  <input type="number" value={createForm.rate_limit_per_minute} onChange={e => setCreateForm({ ...createForm, rate_limit_per_minute: parseInt(e.target.value) || 60 })} min={1} max={10000} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Expiration</label>
                  <select value={createForm.expires_in_days} onChange={e => setCreateForm({ ...createForm, expires_in_days: parseInt(e.target.value) })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm">
                    {EXPIRY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl flex justify-end gap-2">
                <button onClick={() => setCreateOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-200 rounded-lg hover:bg-slate-300">Cancel</button>
                <button onClick={createKey} disabled={saving || !createForm.key_name.trim() || createForm.scopes.length === 0} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1">
                  {saving ? <><RefreshCw size={14} className="animate-spin" /> Creating...</> : <><Key size={14} /> Create Key</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Created Key Display Modal */}
      {createdKeyOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-[9999]">
          <div className="relative min-h-full flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg ml-64">
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-5 rounded-t-2xl">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl"><CheckCircle className="text-white" size={22} /></div>
                  <div>
                    <h3 className="text-lg font-bold text-white">API Key Created</h3>
                    <p className="text-white/70 text-sm">Copy your key now - it will not be shown again</p>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle size={16} className="text-amber-600 flex-shrink-0" />
                  <p className="text-sm text-amber-800 font-medium">This key will not be shown again. Please copy it now and store it securely.</p>
                </div>
                <div className="relative">
                  <code className="block w-full px-4 py-3 bg-slate-900 text-green-400 rounded-lg text-sm font-mono break-all pr-12">{createdFullKey}</code>
                  <button onClick={copyKey} className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-colors ${keyCopied ? 'bg-green-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>
                    {keyCopied ? <CheckCircle size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl flex justify-end">
                <button onClick={() => setCreatedKeyOpen(false)} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">Done</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Key Modal */}
      {editOpen && selectedKey && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-[9999]">
          <div className="relative min-h-full flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg ml-64">
              <div className="bg-gradient-to-r from-amber-600 to-orange-600 px-6 py-5 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-xl"><Edit className="text-white" size={22} /></div>
                    <div>
                      <h3 className="text-lg font-bold text-white">Edit API Key</h3>
                      <p className="text-white/70 text-sm">{selectedKey.key_prefix}...</p>
                    </div>
                  </div>
                  <button onClick={() => setEditOpen(false)} className="p-2 bg-white/20 hover:bg-white/30 rounded-xl"><X className="text-white" size={20} /></button>
                </div>
              </div>
              <div className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Key Name</label>
                  <input type="text" value={editForm.key_name} onChange={e => setEditForm({ ...editForm, key_name: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Scopes</label>
                  <div className="grid grid-cols-2 gap-2">
                    {AVAILABLE_SCOPES.map(scope => (
                      <label key={scope} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${editForm.scopes.includes(scope) ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                        <input type="checkbox" checked={editForm.scopes.includes(scope)} onChange={() => toggleScope(scope, editForm.scopes, s => setEditForm({ ...editForm, scopes: s }))} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                        <span className="text-sm capitalize">{scope}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Rate Limit (per minute)</label>
                  <input type="number" value={editForm.rate_limit_per_minute} onChange={e => setEditForm({ ...editForm, rate_limit_per_minute: parseInt(e.target.value) || 60 })} min={1} max={10000} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <p className="text-sm font-medium text-slate-900">Active</p>
                    <p className="text-xs text-slate-500">Enable or disable this API key</p>
                  </div>
                  <button onClick={() => setEditForm({ ...editForm, is_active: !editForm.is_active })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${editForm.is_active ? 'bg-green-600' : 'bg-slate-300'}`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editForm.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl flex justify-end gap-2">
                <button onClick={() => setEditOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-200 rounded-lg hover:bg-slate-300">Cancel</button>
                <button onClick={updateKey} disabled={saving || !editForm.key_name.trim()} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1">
                  {saving ? <><RefreshCw size={14} className="animate-spin" /> Saving...</> : <><CheckCircle size={14} /> Save Changes</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Usage Modal */}
      {usageOpen && selectedKey && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-[9999]">
          <div className="relative min-h-full flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl ml-64">
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-xl"><BarChart3 className="text-white" size={22} /></div>
                    <div>
                      <h3 className="text-lg font-bold text-white">Usage Analytics</h3>
                      <p className="text-white/70 text-sm">{selectedKey.key_name} ({selectedKey.key_prefix}...)</p>
                    </div>
                  </div>
                  <button onClick={() => setUsageOpen(false)} className="p-2 bg-white/20 hover:bg-white/30 rounded-xl"><X className="text-white" size={20} /></button>
                </div>
              </div>

              {/* Usage Chart Placeholder */}
              <div className="px-6 pt-5">
                <div className="bg-gradient-to-br from-slate-50 to-indigo-50 rounded-xl border border-slate-200 p-6 text-center">
                  <BarChart3 size={32} className="mx-auto mb-2 text-indigo-400" />
                  <p className="text-sm font-medium text-slate-600">API Calls Over Time</p>
                  <p className="text-xs text-slate-400 mt-1">Chart visualization coming soon</p>
                  <div className="flex justify-center gap-6 mt-3">
                    <div className="text-center">
                      <p className="text-xl font-bold text-indigo-600">{usageLogs.length}</p>
                      <p className="text-xs text-slate-500">Total Calls</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-green-600">{usageLogs.filter(l => l.status_code < 400).length}</p>
                      <p className="text-xs text-slate-500">Successful</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-red-600">{usageLogs.filter(l => l.status_code >= 400).length}</p>
                      <p className="text-xs text-slate-500">Errors</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-amber-600">{usageLogs.length > 0 ? Math.round(usageLogs.reduce((a, l) => a + l.response_time_ms, 0) / usageLogs.length) : 0}ms</p>
                      <p className="text-xs text-slate-500">Avg Response</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Calls Table */}
              <div className="p-6 max-h-[50vh] overflow-y-auto">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Recent API Calls</h4>
                {usageLoading ? (
                  <div className="flex items-center justify-center h-24">
                    <RefreshCw className="animate-spin text-blue-600" size={24} />
                  </div>
                ) : paginatedUsage.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Activity size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No usage data recorded</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Endpoint</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Method</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">IP</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Response</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Timestamp</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {paginatedUsage.map(log => (
                            <tr key={log.id} className="hover:bg-slate-50">
                              <td className="px-3 py-2 font-mono text-xs text-slate-700 max-w-[200px] truncate" title={log.endpoint}>{log.endpoint}</td>
                              <td className="px-3 py-2">
                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${getMethodColor(log.method)}`}>{log.method}</span>
                              </td>
                              <td className={`px-3 py-2 font-mono text-xs font-medium ${getStatusColor(log.status_code)}`}>{log.status_code}</td>
                              <td className="px-3 py-2 font-mono text-xs text-slate-500">{log.ip_address}</td>
                              <td className="px-3 py-2 text-xs text-slate-600">{log.response_time_ms}ms</td>
                              <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {totalUsagePages > 1 && (
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
                        <p className="text-xs text-slate-500">Page {usagePage} of {totalUsagePages}</p>
                        <div className="flex gap-2">
                          <button onClick={() => setUsagePage(p => Math.max(1, p - 1))} disabled={usagePage === 1} className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-50"><ChevronLeft size={14} /></button>
                          <button onClick={() => setUsagePage(p => Math.min(totalUsagePages, p + 1))} disabled={usagePage === totalUsagePages} className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-50"><ChevronRight size={14} /></button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl flex justify-end">
                <button onClick={() => setUsageOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-200 rounded-lg hover:bg-slate-300">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Confirmation */}
      <ConfirmationModal
        isOpen={revokeOpen}
        onClose={() => setRevokeOpen(false)}
        onConfirm={revokeKey}
        title="Revoke API Key"
        message={`Are you sure you want to revoke "${selectedKey?.key_name}"? Any applications using this key will immediately lose access. This action cannot be undone.`}
        confirmText="Revoke Key"
        type="danger"
      />
    </div>
  );
};

export default ApiKeysManagement;
